import React, { useState, useEffect } from 'react';

/**
 * RightPanelTabs — a tabbed right-panel shell.
 *
 * Used during the business-plan and SBA phases to put the Financial Data,
 * Business Plan, and (SBA only) SBA Package panels behind tabs in a single
 * right-panel slot. Each tab's content is ALWAYS mounted and toggled with
 * `display` (never conditionally rendered) so panel state — scroll position,
 * in-progress edits, fetched data — survives switching tabs.
 *
 * Props:
 *   tabs:          [{ key, label, render }]   — render() returns the embedded panel
 *   defaultTabKey: which tab is active on first mount
 *   open / onClose: drive the mobile drawer like the other panels
 */
export default function RightPanelTabs({
  tabs,
  defaultTabKey,
  open = true,
  onClose = () => {},
}) {
  const initial = defaultTabKey || (tabs[0] && tabs[0].key);
  const [active, setActive] = useState(initial);

  // If the available tabs change (e.g. the SBA tab appears) and the active one
  // is gone, fall back to the default/first tab.
  useEffect(() => {
    if (!tabs.some((t) => t.key === active)) {
      setActive(defaultTabKey || (tabs[0] && tabs[0].key));
    }
  }, [tabs, active, defaultTabKey]);

  return (
    <aside
      className={`data-panel right-panel-tabs ${open ? 'data-panel-open' : 'data-panel-closed'}`}
      aria-label="Plan panels"
    >
      <div className="right-panel-tabbar" role="tablist">
        {tabs.map((t) => (
          <button
            key={t.key}
            type="button"
            role="tab"
            aria-selected={active === t.key}
            className={`right-panel-tab ${active === t.key ? 'active' : ''}`}
            onClick={() => setActive(t.key)}
          >
            {t.label}
          </button>
        ))}
        <button
          type="button"
          className="data-panel-close-btn right-panel-close-btn"
          aria-label="Close panel"
          onClick={onClose}
        >
          ×
        </button>
      </div>

      {tabs.map((t) => (
        <div
          key={t.key}
          role="tabpanel"
          className="right-panel-tabpane"
          style={{ display: active === t.key ? 'flex' : 'none' }}
        >
          {t.render()}
        </div>
      ))}
    </aside>
  );
}
